import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  TextField,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { Save } from '@mui/icons-material';
import { toast } from 'react-toastify';

const API_BASE_URL = `${process.env.REACT_APP_BACKEND_URL}asrosync/agents`;
const MODEL_CFG_BASE = `${process.env.REACT_APP_BACKEND_URL}asrosync/model-configurations`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

const AgentConfigurationModal = ({ open, onClose, agent, tenantId, onSave, onSuccess }) => {
  const [saving, setSaving] = useState(false);
  const [globalAgents, setGlobalAgents] = useState([]);
  const [formData, setFormData] = useState({
    globalAgentId: '',
    agentName: '',
    agentAppName: '',
    agentMode: 'CHAT-UI',
    agentToken: '',
    description: '',
    agentConfig: '',
    isActive: true,
    modelConfigurationId: '',
  });
  const [modelConfigurations, setModelConfigurations] = useState([]);
  const [errors, setErrors] = useState({});

  const isEditMode = !!agent;

  const fetchModelConfigurations = async () => {
    if (!tenantId) return;
    try {
      const response = await fetch(`${MODEL_CFG_BASE}/tenant/${tenantId}?activeOnly=true`, {
        method: 'GET',
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        setModelConfigurations([]);
        return;
      }
      const data = await response.json();
      setModelConfigurations(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching model configurations:', error);
      setModelConfigurations([]);
    }
  };

  const fetchGlobalAgents = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/global?isActive=true`, {
        method: 'GET',
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setGlobalAgents(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching global agents:', error);
      setGlobalAgents([]);
    }
  };

  useEffect(() => {
    if (open) {
      if (agent) {
        setFormData({
          agentName: agent.agentName || '',
          agentAppName: agent.agentAppName || '',
          agentMode: agent.agentMode || agent.agent_mode || 'CHAT-UI',
          agentToken: agent.agentToken || '',
          description: agent.description || '',
          agentConfig: typeof agent.agentConfig === 'string'
            ? agent.agentConfig
            : JSON.stringify(agent.agentConfig, null, 2),
          isActive: agent.isActive !== undefined ? agent.isActive : true,
          modelConfigurationId: (() => {
            const raw =
              agent.modelConfigurationId ?? agent.model_configuration_id ?? null;
            if (raw == null || raw === '') return '';
            return String(raw);
          })(),
        });
      } else {
        setFormData({
          globalAgentId: '',
          agentName: '',
          agentAppName: '',
          agentMode: 'CHAT-UI',
          agentToken: '',
          description: '',
          agentConfig: '',
          isActive: true,
          modelConfigurationId: '',
        });
      }
      setErrors({});
      fetchModelConfigurations();
      if (!agent) {
        fetchGlobalAgents();
      } else {
        setGlobalAgents([]);
      }
    }
  }, [open, agent, tenantId]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.agentName || formData.agentName.trim().length < 3) {
      newErrors.agentName = 'Agent name is required and must be at least 3 characters';
    }

    if (!formData.agentAppName || formData.agentAppName.trim() === '') {
      newErrors.agentAppName = 'Agent app name is required';
    }

    if (formData.agentConfig) {
      try {
        JSON.parse(formData.agentConfig);
      } catch (e) {
        newErrors.agentConfig = 'Invalid JSON format';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    setSaving(true);
    try {
      const dataToSave = {
        agentName: formData.agentName,
        agentAppName: formData.agentAppName,
        agent_mode: formData.agentMode,
        agentToken: formData.agentToken,
        description: formData.description,
        agentConfig: formData.agentConfig ? JSON.parse(formData.agentConfig) : null,
        isActive: formData.isActive,
      };
      const mid = formData.modelConfigurationId;
      if (isEditMode) {
        dataToSave.modelConfigurationId = mid === '' || mid == null ? 0 : Number(mid);
      } else {
        if (mid !== '' && mid != null) {
          dataToSave.modelConfigurationId = Number(mid);
        }
        const globalAgentId = formData.globalAgentId;
        if (globalAgentId) {
          const selectedGlobal = globalAgents.find(
            (ga) => String(ga.agentId) === String(globalAgentId)
          );
          if (selectedGlobal) {
            dataToSave.globalAgentId = selectedGlobal.agentId;
            if (selectedGlobal.baseUrl) {
              dataToSave.baseUrl = selectedGlobal.baseUrl;
            }
            if (selectedGlobal.webhookEndpoint) {
              dataToSave.webhookEndpoint = selectedGlobal.webhookEndpoint;
            }
          }
        }
      }
      await onSave(dataToSave);
      if (onSuccess) {
        await onSuccess();
      }
      onClose();
    } catch (error) {
      console.error('Error saving agent:', error);
      toast.error(`Error saving agent: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const formContent = (
    <>
      <Grid item xs={12}>
        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
          Agent Information
        </Typography>
      </Grid>

      {!isEditMode && (
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth size="small">
            <InputLabel>Global Agent</InputLabel>
            <Select
              name="globalAgentId"
              value={formData.globalAgentId}
              onChange={handleInputChange}
              label="Global Agent"
            >
              <MenuItem value="">-- Select --</MenuItem>
              {globalAgents.map((ga) => (
                <MenuItem key={ga.agentId} value={String(ga.agentId)}>
                  {ga.agentName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      )}

      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Agent Name *"
          name="agentName"
          value={formData.agentName}
          onChange={handleInputChange}
          error={!!errors.agentName}
          helperText={errors.agentName}
          size="small"
          required
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <FormControl fullWidth size="small" required error={!!errors.agentAppName}>
          <InputLabel>Agent App Name *</InputLabel>
          <Select
            name="agentAppName"
            value={formData.agentAppName}
            onChange={handleInputChange}
            label="Agent App Name *"
          >
            <MenuItem value="">-- Select --</MenuItem>
            <MenuItem value="n8n">n8n</MenuItem>
            <MenuItem value="langchain">langchain</MenuItem>
          </Select>
          {errors.agentAppName && (
            <FormHelperText error>{errors.agentAppName}</FormHelperText>
          )}
        </FormControl>
      </Grid>

      <Grid item xs={12} sm={6}>
        <FormControl fullWidth size="small">
          <InputLabel>Agent Mode</InputLabel>
          <Select
            name="agentMode"
            value={formData.agentMode}
            onChange={handleInputChange}
            label="Agent Mode"
          >
            <MenuItem value="CHAT-UI">CHAT-UI</MenuItem>
            <MenuItem value="INTELLIGENT">INTELLIGENT</MenuItem>
            <MenuItem value="DIRECT">DIRECT</MenuItem>
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12} sm={6}>
        <FormControl fullWidth size="small">
          <InputLabel>LLM model profile</InputLabel>
          <Select
            name="modelConfigurationId"
            value={formData.modelConfigurationId}
            onChange={handleInputChange}
            label="LLM model profile"
          >
            <MenuItem value="">— None (use agent JSON / defaults) —</MenuItem>
            {modelConfigurations.map((m) => (
              <MenuItem key={m.modelConfigurationId} value={String(m.modelConfigurationId)}>
                {m.displayName}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>
            LangChain agents: pick a row from Model configuration. Requires API secret on that profile for runs.
          </FormHelperText>
        </FormControl>
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Agent Token"
          name="agentToken"
          value={formData.agentToken}
          onChange={handleInputChange}
          size="small"
          type="password"
        />
      </Grid>

      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Description"
          name="description"
          value={formData.description}
          onChange={handleInputChange}
          multiline
          rows={3}
          size="small"
        />
      </Grid>

      <Grid item xs={12}>
        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
          Agent Config (JSON)
        </Typography>
      </Grid>

      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Agent Config"
          name="agentConfig"
          value={formData.agentConfig}
          onChange={handleInputChange}
          multiline
          rows={6}
          size="small"
          error={!!errors.agentConfig}
          helperText={errors.agentConfig || 'Enter JSON configuration'}
          sx={{
            '& .MuiInputBase-input': {
              fontFamily: 'monospace',
              fontSize: '0.875rem'
            }
          }}
        />
      </Grid>

      <Grid item xs={12}>
        <FormControlLabel
          control={
            <Switch
              checked={formData.isActive}
              onChange={handleInputChange}
              name="isActive"
            />
          }
          label="Active"
        />
      </Grid>
    </>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      aria-labelledby="agent-config-dialog-title"
    >
      <DialogTitle id="agent-config-dialog-title" sx={{ backgroundColor: 'primary.main', color: 'background.paper' }}>
        {isEditMode ? 'Edit Agent' : 'Create Agent'}
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ pt: 1 }}>
          <Grid container spacing={2}>
            {formContent}
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="outlined" disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} /> : <Save />}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AgentConfigurationModal;
